import React, { Suspense, lazy, useEffect } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { loadDefaultVendor, logout } from '@store/actions';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useAuth0 } from '@auth0/auth0-react';
import { Auth0LoggedInSetState } from './Auth0LoggedInSetState';
import { Loader, LoadingTento } from 'shared-components';
import { useApplyWizardContext } from './context/ApplyWizardContext';
import { STEP_BCM_HOMEPAGE, STEP_HOMEPAGE } from '@components/ApplyWizard/ApplyWizardSteps';
import { ParamsFromUrlWrapper } from '@utils/ParamsFromUrlWrapper';
import { AnimatePresence, motion } from 'motion/react';
import { isSyntheticTest } from '@utils/platformBasedInfo';
import ErrorBoundary from '@components/_shared/Error/Error';
import { IframeOrWindowCleanupState } from './IframeOrWindowCleanupState';
import { EmbedLearnMore } from '@components/EmbedLearnMore/EmbedLearnMore';
import { isStandalonePublicPath } from '@utils/standalonePublicPaths';

const Apply = lazy(() => import('./components/Apply'));
const BCM = lazy(() => import('./components/BCM'));
const EQLeadFlow = lazy(() => import('./components/EQLeadFlow'));
const Version = lazy(() => import('./Version'));
const LoginTest = lazy(() => import('./components/LoginTest/LoginTest').then((m) => ({ default: m.LoginTest })));

const UserCleanup = () => {
  const dispatch = useDispatch();
  const auth0 = useAuth0();
  useEffect(() => {
    if (!auth0.isLoading && !auth0.isAuthenticated) {
      dispatch(logout());
    }
  }, [auth0.isAuthenticated, auth0.isLoading]);
  return null;
};

const BrandHomeComponent = ({ route_home }) => {
  return route_home ? (
    <Route exact path="/">
      <Redirect to={route_home} />
    </Route>
  ) : (
    <Route exact path="/" component={Apply} />
  );
};

export const auth0AuthroizationParams = {
  redirect_uri: window.location.origin,
  audience: encodeURI('https://backend.tento.co'),
};

const AppView = (props) => {
  const { currentPath, dataLayerPush, applicationFlow } = useApplyWizardContext();
  const dispatch = useDispatch();
  let location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(document.location.search);
  const subId = searchParams.get('sub_id');
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect, isLoading } = useAuth0();

  const appRouteDefault = '/app/:step/:property?';
  const appRouteIndexed = '/app/:step/:idx/:property';

  // load default config
  useEffect(() => {
    dispatch(loadDefaultVendor());
  }, []);

  useEffect(() => {
    // Segment Page Data
    const pageEventProps = {
      brand: {
        brandId: props?.brand_params?.id || '',
        brandName: props?.brand_params?.name || '',
        partnerId: props?.brand_params?.partner_id || '',
        partnerName: props?.brand_params?.partner_name || '',
      },
    };
    // console.log('page view', pageEventProps);
    // Name the page view by its route path so Segment/Amplitude no longer
    // buckets it as "Unnamed" (page() was previously called with only
    // properties). All existing properties are preserved unchanged.
    const pageName = location?.pathname || '/';
    if (!isSyntheticTest) {
      analytics.page(pageName, { ...pageEventProps });
    }
  }, [location, subId]);

  // Returning-user session check: the SDK boots as NOT authenticated once its
  // 1-day `auth0.<clientId>.is.authenticated` cookie has expired, even though
  // the refresh token in localStorage is still valid. Recovering the session
  // here is fine everywhere, but the flow resume (push to the "select
  // application" step) must not run on the standalone public forms: that step
  // sends anyone not yet marked logged-in to Auth0, which is how /partners
  // bounced returning visitors to login (2026-10-27).
  const isStandalonePublicPage = isStandalonePublicPath(location.pathname);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const a = await getAccessTokenSilently();
        if (isStandalonePublicPage) {
          return;
        }
        if (applicationFlow === 'apply') {
          history.push('/app/select');
        } else if (applicationFlow === 'bcm') {
          history.push('/bcm2/business/select');
        } else if (applicationFlow === 'bcm-short') {
          history.push('/bcm/business/select');
        }
      } catch (error) {}
    };

    if (!isAuthenticated && !isLoading && window.IS_STORYBOOK !== '1') {
      checkSession();
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, isLoading, isStandalonePublicPage]);

  const isMarketingLanding = currentPath === STEP_HOMEPAGE || currentPath === '/eq';
  // After Auth0 login, the SPA briefly lands at the redirect_uri (origin) with
  // `?code=&state=` before onRedirectCallback navigates to the original URL. We
  // suppress the page render during that window so the marketing landing /
  // apply chrome doesn't flash before the navigation happens.
  const isAuth0Callback = !!(searchParams.get('code') && searchParams.get('state'));

  if (isAuth0Callback) {
    return (
      <div className="bg-[#f2eae4] min-h-screen w-full flex items-center justify-center apply">
        <Loader />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AnimatePresence initial={false}>
        {!isMarketingLanding &&
          (currentPath === STEP_BCM_HOMEPAGE ||
            (window.IS_STORYBOOK === '1' && location.pathname === STEP_BCM_HOMEPAGE)) && (
            <motion.div
              className={'apply-background-content h-screen w-[100vw] absolute'}
              transition={{ duration: 2 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          )}
      </AnimatePresence>
      {process.env.NODE_ENV !== 'test' && !window.IS_STORYBOOK && <UserCleanup />}
      <motion.div
        className={`${
          isMarketingLanding
            ? 'bg-[#f2eae4] min-h-screen'
            : `${
                currentPath.startsWith('/embed') || applicationFlow === 'eqlead'
                  ? 'bg-white'
                  : applicationFlow === 'bcm-short'
                  ? 'bg-tento-gradient bg-cover'
                  : 'bg-dawn'
              } h-screen`
        } apply`}
      >
        <Auth0LoggedInSetState />
        <IframeOrWindowCleanupState />
        <div>
          <Switch>
            <Suspense
              fallback={
                <div className="flex flex-1 items-center">
                  <Loader />
                </div>
              }
            >
              <ParamsFromUrlWrapper dataLayerPush={dataLayerPush}>
                <Route exact path="/embed/learn-more" component={EmbedLearnMore} />
                <Route exact path={'/login-test'} component={LoginTest} />
                <Route exact path={'/bcm'} component={BCM} />
                <Route exact path={'/bcm/business/select'} component={BCM} />
                <Route exact path={'/bcm/app/:step/:property?'} component={BCM} />
                <Route exact path={'/bcm/app/:step/:idx/:property'} component={BCM} />
                <Route exact path={'/eq'} component={EQLeadFlow} />
                {/* <Route exact path={'/eq/business/select'} component={EQLeadFlow} /> */}
                <Route exact path={'/eq/:step/:property?'} component={EQLeadFlow} />
                <Route exact path={'/eq/:step/:idx/:property'} component={EQLeadFlow} />
                <Route exact path={'/eq/trigger-error-boundary'} component={EQLeadFlow} />
                <Route exact path={'/lc/:password?'} component={EQLeadFlow} />
                <Route exact path={'/contact'} component={Apply} />
                <Route exact path={'/partners'} component={Apply} />
                <Route exact path={'/version'} component={Version} />
                <Route exact path={'/app'} component={Apply} />
                <Route exact path={'/bcm2'} component={BCM} />
                <Route exact path={'/login'} component={BCM} />
                <Route exact path={'/bcm2/trigger-error-boundary'} component={BCM} />
                <Route exact path={'/bcm2/business/select'} component={BCM} />
                <Route exact path={'/bcm2/app/:step/:property?'} component={BCM} />
                <Route exact path={'/bcm2/app/:step/:idx/:property'} component={BCM} />
                <Route exact path={appRouteDefault} component={Apply} />
                <Route exact path={appRouteIndexed} component={Apply} />
                <Route exact path={'/continue/direct-upload/:dealId/:accountId/:mainContactId?'} component={Apply} />
                <Route exact path={'/continue/direct-upload/:dealId/:accountId/:mainContactId?'} component={Apply} />
                <Route exact path={'/continue/direct-upload'} component={Apply} />
                <Route exact path={'/continue/plaid-connect'} component={Apply} />
                <Route exact path={'/continue/plaid'} component={Apply} />
                <Route exact path={'/continue/upload'} component={Apply} />
                <Route exact path={'/continue/plaid-and-upload'} component={Apply} />
                <BrandHomeComponent route={props.route_home} />
              </ParamsFromUrlWrapper>
            </Suspense>
          </Switch>
        </div>
      </motion.div>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state) => {
  return {
    route_home: state.brand?.data?.route_home,
    brand_params: state.brand?.data?.brand,
  };
};

export const App = connect(mapStateToProps)(AppView);
